import { ChevronLeft, ChevronRight } from '@atom-learning/icons'
import type { Props as DayzedInterface } from 'dayzed'
import { useDayzed } from 'dayzed'
import * as React from 'react'

import { ActionIcon } from '~/components/action-icon/ActionIcon'
import { Button } from '~/components/button/Button'
import { Icon } from '~/components/icon/Icon'
import { Text } from '~/components/text/Text'
import { styled } from '~/styled'
import { cn } from '~/styled'

import { DEFAULT_LABELS, monthNamesShort, weekdayNamesShort } from './constants'
import { Day } from './Day'

const StyledButton = styled(Button, {
  base: ['text-grey-1000', 'p-3', 'w-16'],
  variants: {
    selected: {
      false: [
        'disabled:bg-white!',
        'font-normal',
        'hover:bg-grey-200!',
        'hover:text-grey-1000!',
        'text-grey-1000!'
      ]
    }
  }
})

export type CalendarTranslationProps = {
  monthNames?: string[]
  weekdayNames?: string[]
  labels?: {
    open: string
    next: string
    previous: string
    nextYear: string
    previousYear: string
  }
}

type CalendarProps = DayzedInterface &
  CalendarTranslationProps & {
    refDateToday?: React.RefObject<HTMLButtonElement | null>
    refDateSelected?: React.RefObject<HTMLButtonElement | null>
    setYear: (date: Date) => Promise<void>
    className?: string
  }

const offsetWeekdayNames = (
  weekdayNames: string[],
  firstDayOfWeek: number
): string[] => {
  const start = weekdayNames.slice(0, firstDayOfWeek)
  const end = weekdayNames.slice(firstDayOfWeek)
  return end.concat(start)
}

export const Calendar = ({
  className,
  refDateSelected,
  refDateToday,
  firstDayOfWeek = 0,
  monthNames = monthNamesShort,
  weekdayNames = weekdayNamesShort,
  labels = DEFAULT_LABELS,
  date = new Date(),
  minDate,
  maxDate,
  setYear,
  ...remainingProps
}: CalendarProps) => {
  const [showYears, setShowYears] = React.useState<boolean>(false)
  const [currentYear, setCurrentYear] = React.useState<number>(
    date?.getFullYear()
  )

  const handleSetYear = (year: number): void => {
    const newDate = date
    newDate.setFullYear(year)
    setYear(newDate)
    setShowYears(false)
  }

  const isAtMinYear = minDate && currentYear - 16 <= minDate.getFullYear()
  const isAtMaxYear = maxDate && currentYear >= maxDate.getFullYear()

  const yearList = Array.from({ length: 16 }, (_, i) => {
    const year = currentYear - i
    if (
      (maxDate && year > maxDate.getFullYear()) ||
      (minDate && year < minDate.getFullYear())
    )
      return 0
    return year
  })

  const { calendars, getBackProps, getForwardProps, getDateProps } = useDayzed({
    firstDayOfWeek,
    showOutsideDays: true,
    date,
    minDate,
    maxDate,
    ...remainingProps
  })

  if (!calendars.length) return null

  return (
    <div className={cn('relative', 'w-fit', '-mt-1', className)}>
      <div className="absolute top-0 -right-1 flex">
        <ActionIcon
          label={labels[showYears ? 'previousYear' : 'previous']}
          theme="neutral"
          size="md"
          {...(!showYears && getBackProps({ calendars }))}
          {...(showYears && {
            onClick: () => setCurrentYear(currentYear - 16)
          })}
          disabled={showYears && isAtMinYear}
        >
          <Icon is={ChevronLeft} />
        </ActionIcon>
        <ActionIcon
          label={labels[showYears ? 'nextYear' : 'next']}
          theme="neutral"
          size="md"
          {...(!showYears && getForwardProps({ calendars }))}
          {...(showYears && {
            onClick: () => setCurrentYear(currentYear + 16)
          })}
          disabled={showYears && isAtMaxYear}
        >
          <Icon is={ChevronRight} />
        </ActionIcon>
      </div>
      {showYears && (
        <div className="grid grid-cols-4 gap-x-1 gap-y-3 pt-12 [direction:rtl]">
          {yearList.map((year, i) => {
            const isCurrentYear = year === date.getFullYear()
            if (!year) return <div key={i} className="h-10 w-16" />

            return (
              <StyledButton
                key={`${year}${i}`}
                theme={isCurrentYear ? 'primary' : 'neutral'}
                onClick={() => handleSetYear(year)}
                selected={isCurrentYear}
              >
                {year}
              </StyledButton>
            )
          })}
        </div>
      )}
      {!showYears &&
        calendars.map(({ month, year, weeks }) => (
          <div key={`${month}${year}`}>
            <div className="mb-4 flex h-10 items-center">
              <Button
                theme="neutral"
                onClick={() => setShowYears(true)}
                className="text-grey-1000 px-0"
              >
                {monthNames[month]} {year}
              </Button>
            </div>
            <div className="mb-3 grid grid-cols-7 gap-x-2 gap-y-1">
              {offsetWeekdayNames(weekdayNames, firstDayOfWeek).map(
                (weekday) => (
                  <Text
                    as="span"
                    size="sm"
                    weight="bold"
                    key={`${month}${year}${weekday}`}
                    className="text-center"
                  >
                    {weekday}
                  </Text>
                )
              )}
            </div>
            <div className="grid grid-cols-7 gap-x-2 gap-y-1">
              {weeks.map((week, weekIndex) =>
                week.map((dateObj, index) => {
                  const key = `${month}${year}${weekIndex}${index}`

                  if (!dateObj) return <div key={key} />

                  const { date, selected, today, prevMonth, nextMonth } =
                    dateObj

                  return (
                    <Day
                      isOutsideMonth={prevMonth || nextMonth}
                      isSelected={selected}
                      isToday={today}
                      key={key}
                      ref={
                        selected ? refDateSelected : today ? refDateToday : null
                      }
                      {...getDateProps({ dateObj })}
                      type="button"
                    >
                      {date.getDate()}
                    </Day>
                  )
                })
              )}
            </div>
          </div>
        ))}
    </div>
  )
}

Calendar.displayName = 'Calendar'
